// src/pages/afipQueuePage.js
/**
 * AFIP Queue Page - Combined view showing Queue Status and Queue List
 * Two-column layout: Queue Status (left) and Queue List (right)
 */

import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Button, Space, message, Modal, Alert, Spin, Empty, Typography, Tooltip } from 'antd';
import { 
    ClockCircleOutlined, 
    CheckCircleOutlined, 
    CloseCircleOutlined, 
    SyncOutlined,
    ReloadOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    CalendarOutlined,
    DollarOutlined,
    UserOutlined,
    EyeOutlined
} from '@ant-design/icons';
import afipQueueService from '../services/afipQueueService';
import { getInvoiceHistory } from '../services/invoiceService';
import { useAuth } from '../context/AuthContext';
import { isAdminRole } from '../permissions';
import './invoicesPage.css'; // Reuse styling from invoices/presupuestos pages

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { confirm } = Modal;

const AFIPQueuePage = () => {
    const { user } = useAuth();
    const isAdmin = isAdminRole(user?.role);
    const [stats, setStats] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingRetry, setLoadingRetry] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('all');
    // Cross-reference map: external_reference -> { id, numero_comprobante }
    const [externalRefMap, setExternalRefMap] = useState({});

    // Fetch data on mount only
    useEffect(() => {
        fetchData();
    }, []);

    /**
     * Fetch queue statistics and invoices
     */
    const fetchData = async () => {
        setLoading(true);
        
        try {
            // Fetch stats
            const statsResult = await afipQueueService.getStats();
            console.log('📊 Stats Result:', statsResult);
            if (statsResult.success) {
                console.log('📊 Stats Data:', statsResult.data);
                // Backend returns nested structure: {success: true, data: {success: true, data: {stats...}}}
                const statsData = statsResult.data.data || statsResult.data.stats || statsResult.data;
                console.log('📊 Stats Object:', statsData);
                setStats(statsData);
            }
            
            // Fetch invoices
            const invoicesResult = await afipQueueService.getMyInvoices({
                limit: 50
            });
            console.log('📋 Invoices Result:', invoicesResult);
            
            if (invoicesResult.success) {
                console.log('📋 Invoices Data:', invoicesResult.data);
                // Backend returns nested structure: {success: true, data: {success: true, data: []}}
                const invoicesArray = invoicesResult.data.data || invoicesResult.data.invoices || [];
                console.log('📋 Invoices Array:', invoicesArray);
                setInvoices(invoicesArray);

                // Cross-reference: fetch invoices endpoint to resolve numero_comprobante for approved items
                const approvedItems = invoicesArray.filter(inv => inv.status === 'approved' || inv.status === 'completed');
                if (approvedItems.length > 0) {
                    try {
                        const historyResult = await getInvoiceHistory({ page: 1, limit: 100 });
                        const invoicesData = historyResult?.data?.invoices || [];
                        const map = {};
                        invoicesData.forEach(inv => {
                            if (inv.external_reference) {
                                map[inv.external_reference] = {
                                    id: inv.id,
                                    numero_comprobante: inv.numero_comprobante
                                };
                            }
                        });
                        setExternalRefMap(map);
                        console.log('🔗 External ref map built:', Object.keys(map).length, 'entries');
                    } catch (e) {
                        console.warn('Could not build invoice cross-reference map:', e);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching queue data:', error);
            message.error('Error al cargar datos de la cola');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle sync processing invoices with TusFacturas
     */
    const handleSyncProcessing = async () => {
        setLoading(true);
        
        try {
            const result = await afipQueueService.syncProcessingInvoices();
            
            if (result.success) {
                const { synced, failed, skipped, message: syncMessage } = result.data;
                
                // Show success message with details
                message.success(
                    `✅ ${syncMessage || `Sync completado: ${synced} sincronizadas, ${failed} fallidas, ${skipped} omitidas`}`,
                    5
                );
                
                // Refresh data after sync
                fetchData();
            } else {
                message.error(result.message || 'Error al sincronizar facturas');
            }
        } catch (error) {
            console.error('Error syncing processing invoices:', error);
            message.error('Error al sincronizar facturas en procesamiento');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle retry for failed invoice
     */
    const handleRetry = async (queueId) => {
        confirm({
            title: '¿Reintentar factura?',
            icon: <ExclamationCircleOutlined />,
            content: 'La factura será reenviada a la cola de procesamiento AFIP.',
            okText: 'Reintentar',
            cancelText: 'Cancelar',
            onOk: async () => {
                setLoadingRetry(queueId);
                
                try {
                    const result = await afipQueueService.retryInvoice(queueId);
                    
                    if (result.success) {
                        message.success('Factura reenviada a la cola');
                        fetchData(); // Refresh data
                    } else {
                        message.error(result.message || 'Error al reintentar');
                    }
                } catch (error) {
                    message.error('Error al reintentar factura');
                } finally {
                    setLoadingRetry(null);
                }
            }
        });
    };

    /**
     * Get status tag color
     */
    const getStatusColor = (status) => {
        const colors = {
            pending: 'default',
            processing: 'processing',
            completed: 'success',
            approved: 'success',
            failed: 'error'
        };
        return colors[status] || 'default';
    };

    /**
     * Get status text
     */
    const getStatusText = (status) => {
        const texts = {
            pending: 'Pendiente',
            processing: 'Procesando',
            completed: 'APROBADA',
            approved: 'APROBADA',
            failed: 'Error'
        };
        return texts[status] || status;
    };

    /**
     * Get status icon
     */
    const getStatusIcon = (status) => {
        const icons = {
            pending: <ClockCircleOutlined />,
            processing: <SyncOutlined spin />,
            completed: <CheckCircleOutlined />,
            failed: <CloseCircleOutlined />
        };
        return icons[status] || null;
    };

    /**
     * Table columns configuration
     */
    const columns = [
        {
            title: 'Referencia',
            dataIndex: 'external_reference',
            key: 'external_reference',
            width: 200,
            render: (text) => (
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {text}
                </span>
            )
        },
        {
            title: 'Cliente',
            dataIndex: 'client_name',
            key: 'client_name',
            width: 200,
            ellipsis: true,
            render: (text) => text || 'Consumidor Final'
        },
        {
            title: 'Tipo',
            key: 'invoice_type',
            width: 120,
            render: (_, record) => {
                const tipoComprobante = record.invoice_type || record.tipo_comprobante || '';

                if (tipoComprobante.includes('NOTA DE CREDITO') || tipoComprobante.includes('NC')) {
                    return <Tag color="red">Nota de Crédito</Tag>;
                }
                if (tipoComprobante === 'PRESUPUESTO' || record.status === 'PRESUPUESTO') {
                    return <Tag color="blue">Factura X</Tag>;
                }
                if (tipoComprobante === 'FACTURA A' || tipoComprobante === 'A') {
                    return <Tag color="green">Factura A</Tag>;
                }
                if (tipoComprobante === 'FACTURA B' || tipoComprobante === 'B') {
                    return <Tag color="orange">Factura B</Tag>;
                }
                if (tipoComprobante === 'FACTURA C' || tipoComprobante === 'C') {
                    return <Tag color="cyan">Factura C</Tag>;
                }
                if (tipoComprobante.includes('FACTURA')) {
                    return <Tag color="green">Factura</Tag>;
                }
                return <Tag color="default">{tipoComprobante || 'Sin especificar'}</Tag>;
            }
        },
        {
            title: 'Importe',
            dataIndex: 'invoice_amount',
            key: 'invoice_amount',
            width: 120,
            align: 'right',
            render: (amount) => (
                <span style={{ fontWeight: 'bold' }}>
                    ${parseFloat(amount || 0).toFixed(2)}
                </span>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 140,
            align: 'center',
            render: (status) => (
                <Tag 
                    icon={getStatusIcon(status)} 
                    color={getStatusColor(status)}
                >
                    {getStatusText(status)}
                </Tag>
            )
        },
        {
            title: 'Fecha',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (date) => new Date(date).toLocaleString('es-AR')
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 100,
            fixed: 'right',
            align: 'center',
            render: (_, record) => {
                if (record.status !== 'approved' && record.status !== 'completed') return null;
                // Resolve invoice id via external_reference cross-reference, fallback to invoice_id
                const resolvedId = externalRefMap[record.external_reference]?.id || record.invoice_id;
                return (
                    <Tooltip title="Ver Factura">
                        <Button
                            type="default"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => {
                                if (isDemoMode) { message.info('Visualización de Factura aprobada por AFIP con CAE y número de comprobante'); return; }
                                window.open(`#/print-quote?invoiceId=${resolvedId}`, '_blank');
                            }}
                        >
                            Ver Factura
                        </Button>
                    </Tooltip>
                );
            }
        }
    ];

    return (
        <div className="invoices-page-container">
            <div className="invoices-header">
                <div>
                    <h1>📋 Facturas en Cola</h1>
                    <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
                        Sistema de cola asíncrona para facturación cuando AFIP no está disponible
                    </p>
                </div>
                <Button
                    type="primary"
                    danger={stats?.processing > 0 || stats?.pending > 0}
                    style={stats?.processing === 0 && stats?.pending === 0 ? { backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' } : {}}
                    icon={<SyncOutlined spin={loading} />}
                    onClick={isDemoMode ? () => message.info('Sincronización de facturas pendientes con el servidor AFIP') : handleSyncProcessing}
                    loading={loading}
                >
                    Sincronizar con AFIP
                </Button>
            </div>

            {/* Statistics Cards — admin only */}
            {isAdmin && <Row gutter={16} style={{ marginBottom: 24, display: 'flex' }}>
                <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                    <Card size="small" style={{ borderLeft: '4px solid #1890ff', width: '100%' }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                        ) : (
                            <Statistic
                                title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>TOTAL EN COLA</Typography.Text>}
                                value={(stats?.pending || 0) + (stats?.processing || 0)}
                                valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                            />
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                    <Card size="small" style={{ borderLeft: '4px solid #faad14', width: '100%' }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                        ) : (
                            <Statistic
                                title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>PENDIENTES</Typography.Text>}
                                value={stats?.pending || 0}
                                valueStyle={{ color: '#faad14', fontSize: '24px' }}
                            />
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                    <Card size="small" style={{ borderLeft: '4px solid #faad14', width: '100%' }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                        ) : (
                            <Statistic
                                title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>PROCESANDO</Typography.Text>}
                                value={stats?.processing || 0}
                                valueStyle={{ color: '#faad14', fontSize: '24px' }}
                            />
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                    <Card size="small" style={{ borderLeft: '4px solid #52c41a', width: '100%' }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                        ) : (
                            <Statistic
                                title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>COMPLETADAS</Typography.Text>}
                                value={invoices.filter(inv => inv.status === 'approved' || inv.status === 'completed').length}
                                valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                            />
                        )}
                    </Card>
                </Col>
            </Row>}

            {/* Alert if there are failed invoices */}
            {isAdmin && stats && stats.failed_today > 0 && (
                <Alert
                    type="error"
                    message={`${stats.failed_today} factura${stats.failed_today > 1 ? 's' : ''} con error`}
                    description="Hay facturas que fallaron en el procesamiento. Revise los detalles y reintente si es necesario."
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* Filters */}
            <div className="invoices-filters" style={{ marginBottom: 16 }}>
                <Space wrap>
                    <Button
                        type={selectedStatus === 'all' ? 'primary' : 'default'}
                        onClick={() => setSelectedStatus('all')}
                    >
                        Todas
                    </Button>
                    <Button
                        type={selectedStatus === 'pending' ? 'primary' : 'default'}
                        onClick={() => setSelectedStatus('pending')}
                    >
                        Pendientes
                    </Button>
                    <Button
                        type={selectedStatus === 'processing' ? 'primary' : 'default'}
                        onClick={() => setSelectedStatus('processing')}
                    >
                        Procesando
                    </Button>
                    <Button
                        type={selectedStatus === 'failed' ? 'primary' : 'default'}
                        onClick={() => setSelectedStatus('failed')}
                    >
                        Con Error
                    </Button>
                </Space>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={selectedStatus === 'all' ? invoices : invoices.filter(inv => inv.status === selectedStatus)}
                loading={loading}
                rowKey="queueId"
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} facturas`
                }}
                locale={{
                    emptyText: (
                        <Empty
                            description="No hay facturas en cola"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    )
                }}
                scroll={{ x: 1200 }}
            />
        </div>
    );
};

export default AFIPQueuePage;
